import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Divider, Typography, Switch } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LayersOutlined, WebAssetOutlined, TextFieldsOutlined, SmartButtonOutlined, ImageOutlined,
  TableRowsOutlined, ViewColumnOutlined,
  VerticalAlignTopOutlined, VerticalAlignCenterOutlined, VerticalAlignBottomOutlined,
  AlignHorizontalLeftOutlined, AlignHorizontalCenterOutlined, AlignHorizontalRightOutlined,
  SpaceBarOutlined,
  TitleOutlined, LinkOutlined, PublicOutlined, StarOutlined, CheckCircleOutlined,
  AutoAwesomeOutlined, WidgetsOutlined,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { PRIMARY, CONTAINER_COLOR } from '../constants';
import { getEffectiveStyle } from '../utils';
import { InspectorSection, StyleRow, StyleInput, StyleSelect, PxInput, ColorInput, PanelHeader, VisibilityControl } from '../ui/primitives';
import { MediaField } from '../../lobbyBuilderTab/MediaField';
import { SpacingControl, RadiusControl, BorderControl, ShadowControl, DimensionControl } from '../ui/controls';
import { GradientBuilder } from '../ui/GradientBuilder';
import { ElementPalette } from '../canvas/elements';
import { api } from '../../../../hooks/useApi';
import { PAGE_TEMPLATES } from './templates';
import { useTemplateThumbnails } from './templates/useTemplateThumbnails';


// ─── Shared background section ────────────────────────────────────────────────
export function BackgroundSection({ s, bgType, setBgType, updateStyle, accentColor }) {
  const bg = s.background || '';

  const BgTypeBtn = ({ type, label }) => (
    <Box onClick={() => {
      setBgType(type);
      if (type === 'solid')    updateStyle('background', '#ffffff');
      if (type === 'gradient') updateStyle('background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      if (type === 'image')    updateStyle('background', '');
    }} sx={{
      flex: 1, py: 0.6, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600,
      borderRadius: 1, cursor: 'pointer', transition: 'all 0.1s',
      background: bgType === type ? accentColor : 'transparent',
      color: bgType === type ? '#fff' : '#7a8494',
      '&:hover': { color: bgType === type ? '#fff' : accentColor },
    }}>{label}</Box>
  );

  const bgImageUrl = bg.match(/url\(['"]?([^'"()]+)['"]?\)/)?.[1] || '';
  const setBgImageUrl = (url) => updateStyle('background', url ? `url('${url}') center/cover no-repeat` : '');

  return (
    <InspectorSection label="Background">
      <Box sx={{ display: 'flex', background: '#f0f4f8', borderRadius: 1.5, p: '3px', gap: '3px', mb: 1.5 }}>
        <BgTypeBtn type="solid" label="Solid" />
        <BgTypeBtn type="gradient" label="Gradient" />
        <BgTypeBtn type="image" label="Image" />
      </Box>

      {bgType === 'solid' && (
        <StyleRow label="Color">
          <ColorInput value={bg.includes('gradient') || bg.startsWith('url') ? '#ffffff' : bg} onChange={v => updateStyle('background', v)} />
        </StyleRow>
      )}

      {bgType === 'gradient' && (
        <GradientBuilder
          value={bg.includes('gradient') ? bg : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}
          onChange={v => updateStyle('background', v)}
        />
      )}

      {bgType === 'image' && (
        <>
          <StyleRow label="Image">
            <MediaField value={bgImageUrl} onChange={setBgImageUrl} mediaType="image" />
          </StyleRow>
          <StyleRow label="Size">
            <StyleSelect value={s.backgroundSize || 'cover'} onChange={v => updateStyle('backgroundSize', v)}
              options={[{ value: 'cover', label: 'Cover' }, { value: 'contain', label: 'Contain' }, { value: '100% 100%', label: 'Stretch' }, { value: 'auto', label: 'Auto' }]} />
          </StyleRow>
          <StyleRow label="Position">
            <StyleSelect value={s.backgroundPosition || 'center'} onChange={v => updateStyle('backgroundPosition', v)}
              options={[{ value: 'center', label: 'Center' }, { value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }, { value: 'left center', label: 'Left' }, { value: 'right center', label: 'Right' }]} />
          </StyleRow>
          {bgImageUrl && (
            <Box sx={{ mt: 0.5, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${alpha(accentColor, 0.2)}`, height: 80 }}>
              <Box sx={{ width: '100%', height: '100%', background: `url('${bgImageUrl}') center/cover no-repeat` }} />
            </Box>
          )}
        </>
      )}
    </InspectorSection>
  );
}

// ─── SectionSettings ──────────────────────────────────────────────────────────
export function SectionSettings({ section, onUpdate, onClose }) {
  const s = section.style || {};
  const updateStyle = (key, val) => onUpdate({ ...section, style: { ...s, [key]: val } });
  const updateProp  = (key, val) => onUpdate({ ...section, [key]: val });

  const bg = s.background || '';
  const detectBgType = () => {
    if (bg.startsWith('url(') || s.backgroundImage?.startsWith('url(')) return 'image';
    if (bg.includes('gradient')) return 'gradient';
    return 'solid';
  };
  const [bgType, setBgType] = useState(detectBgType);

  const layout   = section.layout   || 'full';
  const maxWidth = section.maxWidth || '1200px';
  const maxWNum  = parseInt(maxWidth) || 1200;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<LayersOutlined sx={{ fontSize: 14 }} />} label="Section" color={PRIMARY} onClose={onClose} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Layout">
          <StyleRow label="Width">
            <StyleSelect value={layout} onChange={v => updateProp('layout', v)}
              options={[{ value: 'full', label: 'Full width' }, { value: 'boxed', label: 'Boxed' }]} />
          </StyleRow>
          {layout === 'boxed' && (
            <>
              <StyleRow label="Max width">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input type="number" value={maxWNum} min={320} max={1920}
                    onChange={e => updateProp('maxWidth', (e.target.value || 320) + 'px')}
                    style={{ width: 64, border: '1px solid #e4e9f0', borderRadius: 6, padding: '4px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb' }}
                    onFocus={e => e.target.style.borderColor = PRIMARY}
                    onBlur={e => { e.target.style.borderColor = '#e4e9f0'; updateProp('maxWidth', (parseInt(e.target.value) || 1200) + 'px'); }} />
                  <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', flexShrink: 0 }}>px</Typography>
                </Box>
              </StyleRow>
              <input type="range" min={320} max={1920} step={10} value={maxWNum}
                onChange={e => updateProp('maxWidth', e.target.value + 'px')}
                style={{ width: '100%', cursor: 'pointer', accentColor: PRIMARY, marginTop: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#c0c8d4' }}>320</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#c0c8d4' }}>1920</Typography>
              </Box>
            </>
          )}
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <BackgroundSection s={s} bgType={bgType} setBgType={setBgType} updateStyle={updateStyle} accentColor={PRIMARY} />

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Spacing">
          <SpacingControl label="Padding" style={s} onChange={(k, v) => updateStyle(k, v)} type="padding" />
          <Box sx={{ mt: 0.75 }}><DimensionControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={section.visibleOn} onChange={v => onUpdate({ ...section, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}

// ─── ContainerSettings ────────────────────────────────────────────────────────
export function ContainerSettings({ el, breakpoint = 'desktop', onUpdate, onClose }) {
  const orientation = el.orientation || 'column';
  const s = getEffectiveStyle(el, breakpoint);

  const updateStyle = (key, val) => {
    if (breakpoint === 'desktop') {
      onUpdate({ ...el, style: { ...(el.style || {}), [key]: val } });
    } else {
      onUpdate({ ...el, responsiveStyles: { ...(el.responsiveStyles || {}), [breakpoint]: { ...(el.responsiveStyles?.[breakpoint] || {}), [key]: val } } });
    }
  };

  const isCol = orientation === 'column';
  const alignYVal = s.justifyContent || 'flex-start';
  const alignXVal = s.alignItems     || 'stretch';

  const alignYOptions = isCol ? [
    { value: 'flex-start',    icon: <VerticalAlignTopOutlined    sx={{ fontSize: 16 }} />, tip: 'Top'           },
    { value: 'center',        icon: <VerticalAlignCenterOutlined sx={{ fontSize: 16 }} />, tip: 'Center'        },
    { value: 'flex-end',      icon: <VerticalAlignBottomOutlined sx={{ fontSize: 16 }} />, tip: 'Bottom'        },
    { value: 'space-between', icon: <SpaceBarOutlined            sx={{ fontSize: 16 }} />, tip: 'Space between' },
  ] : [
    { value: 'flex-start', icon: <VerticalAlignTopOutlined    sx={{ fontSize: 16 }} />, tip: 'Top'    },
    { value: 'center',     icon: <VerticalAlignCenterOutlined sx={{ fontSize: 16 }} />, tip: 'Center' },
    { value: 'flex-end',   icon: <VerticalAlignBottomOutlined sx={{ fontSize: 16 }} />, tip: 'Bottom' },
    { value: 'stretch',    icon: <Box sx={{ fontSize: '9px', fontWeight: 700, lineHeight: 1 }}>STR</Box>, tip: 'Stretch' },
  ];

  const alignXOptions = isCol ? [
    { value: 'flex-start', icon: <AlignHorizontalLeftOutlined   sx={{ fontSize: 16 }} />, tip: 'Left'    },
    { value: 'center',     icon: <AlignHorizontalCenterOutlined sx={{ fontSize: 16 }} />, tip: 'Center'  },
    { value: 'flex-end',   icon: <AlignHorizontalRightOutlined  sx={{ fontSize: 16 }} />, tip: 'Right'   },
    { value: 'stretch',    icon: <Box sx={{ fontSize: '9px', fontWeight: 700, lineHeight: 1 }}>STR</Box>, tip: 'Stretch' },
  ] : [
    { value: 'flex-start',    icon: <AlignHorizontalLeftOutlined   sx={{ fontSize: 16 }} />, tip: 'Left'         },
    { value: 'center',        icon: <AlignHorizontalCenterOutlined sx={{ fontSize: 16 }} />, tip: 'Center'       },
    { value: 'flex-end',      icon: <AlignHorizontalRightOutlined  sx={{ fontSize: 16 }} />, tip: 'Right'        },
    { value: 'space-between', icon: <SpaceBarOutlined             sx={{ fontSize: 16 }} />, tip: 'Space between' },
  ];

  const bg = s.background || '';
  const detectBgType = () => {
    if (bg.startsWith('url(') || s.backgroundImage?.startsWith('url(')) return 'image';
    if (bg.includes('gradient')) return 'gradient';
    return 'solid';
  };
  const [bgType, setBgType] = useState(detectBgType);

  const bgImageUrl = bg.match(/url\(['"]?([^'"()]+)['"]?\)/)?.[1] || '';

  const AlignBtn = ({ options, currentVal, styleKey }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {options.map(opt => (
        <Tooltip key={opt.value} title={opt.tip} placement="top">
          <Box onClick={() => updateStyle(styleKey, currentVal === opt.value ? '' : opt.value)}
            sx={{ flex: 1, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.1s', background: currentVal === opt.value ? CONTAINER_COLOR : '#f0f4f8', color: currentVal === opt.value ? '#fff' : '#7a8494', border: `1px solid ${currentVal === opt.value ? CONTAINER_COLOR : '#e8edf2'}`, '&:hover': { background: currentVal === opt.value ? CONTAINER_COLOR : '#e4e9f0' } }}>
            {opt.icon}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<WebAssetOutlined sx={{ fontSize: 14 }} />} label="Container" color={CONTAINER_COLOR} onClose={onClose} breakpoint={breakpoint} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Layout">
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mb: 0.5 }}>Direction</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { value: 'column', icon: <TableRowsOutlined sx={{ fontSize: 16 }} />, tip: 'Column ↓' },
                { value: 'row',    icon: <ViewColumnOutlined sx={{ fontSize: 16 }} />, tip: 'Row →'    },
              ].map(opt => (
                <Tooltip key={opt.value} title={opt.tip} placement="top">
                  <Box onClick={() => {
                    if (breakpoint === 'desktop') {
                      onUpdate({ ...el, orientation: opt.value, style: { ...(el.style || {}), flexDirection: opt.value } });
                    } else {
                      onUpdate({ ...el, responsiveStyles: { ...(el.responsiveStyles || {}), [breakpoint]: { ...(el.responsiveStyles?.[breakpoint] || {}), flexDirection: opt.value } } });
                    }
                  }} sx={{ flex: 1, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.1s', background: orientation === opt.value ? CONTAINER_COLOR : '#f0f4f8', color: orientation === opt.value ? '#fff' : '#7a8494', border: `1px solid ${orientation === opt.value ? CONTAINER_COLOR : '#e8edf2'}`, '&:hover': { background: orientation === opt.value ? CONTAINER_COLOR : '#e4e9f0' } }}>
                    {opt.icon}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mb: 0.5 }}>Align Y</Typography>
            <AlignBtn options={alignYOptions} currentVal={alignYVal} styleKey="justifyContent" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mb: 0.5 }}>Align X</Typography>
            <AlignBtn options={alignXOptions} currentVal={alignXVal} styleKey="alignItems" />
          </Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Spacing">
          <SpacingControl label="Padding" style={s} onChange={(k, v) => updateStyle(k, v)} type="padding" />
          <Box sx={{ mt: 0.75 }}><SpacingControl label="Margin" style={s} onChange={(k, v) => updateStyle(k, v)} type="margin" /></Box>
          <Box sx={{ mt: 0.75 }}><StyleRow label="Gap"><PxInput value={s.gap} onChange={v => updateStyle('gap', v)} placeholder="0" /></StyleRow></Box>
          <Box sx={{ mt: 0.75 }}><DimensionControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <BackgroundSection s={s} bgType={bgType} setBgType={setBgType} updateStyle={updateStyle} accentColor={CONTAINER_COLOR} />

        {/* Overlay — only shown for image bg type */}
        {bgType === 'image' && bgImageUrl && (
          <>
            <Divider sx={{ borderColor: '#f0f2f5' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Overlay</Typography>
              <Box onClick={() => updateStyle('overlayBackground', s.overlayBackground ? '' : 'rgba(0,0,0,0.4)')}
                sx={{ width: 32, height: 18, borderRadius: 9, background: s.overlayBackground ? CONTAINER_COLOR : '#d0d7e2', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                <Box sx={{ position: 'absolute', top: 2, left: s.overlayBackground ? 14 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </Box>
            </Box>
            {s.overlayBackground && (
              <StyleRow label="Color">
                <ColorInput value={s.overlayBackground?.match(/rgba?\([^)]+\)/)?.[0] || 'rgba(0,0,0,0.4)'} onChange={v => updateStyle('overlayBackground', v)} />
              </StyleRow>
            )}
          </>
        )}

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Appearance">
          <RadiusControl style={s} onChange={(k, v) => updateStyle(k, v)} />
          <Box sx={{ mt: 0.75 }}><BorderControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
          <Box sx={{ mt: 0.75 }}><ShadowControl value={s.boxShadow} onChange={v => updateStyle('boxShadow', v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={el.visibleOn} onChange={v => onUpdate({ ...el, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}

// ─── TextInspector ────────────────────────────────────────────────────────────
export function TextInspector({ el, breakpoint = 'desktop', onUpdate, onClose }) {
  const s = getEffectiveStyle(el, breakpoint);
  const updateStyle = (key, val) => {
    if (breakpoint === 'desktop') { onUpdate({ ...el, style: { ...(el.style || {}), [key]: val } }); }
    else { onUpdate({ ...el, responsiveStyles: { ...(el.responsiveStyles || {}), [breakpoint]: { ...(el.responsiveStyles?.[breakpoint] || {}), [key]: val } } }); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<TextFieldsOutlined sx={{ fontSize: 14 }} />} label="Text" color={PRIMARY} onClose={onClose} breakpoint={breakpoint} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Content">
          <textarea value={el.content || ''} onChange={e => onUpdate({ ...el, content: e.target.value })} rows={3}
            style={{ width: '100%', border: '1px solid #e4e9f0', borderRadius: 6, padding: '6px 8px', fontSize: '12px', outline: 'none', fontFamily: 'inherit', background: '#f8f9fb', resize: 'vertical', boxSizing: 'border-box' }} />
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Typography">
          <StyleRow label="Size"><PxInput value={s.fontSize} onChange={v => updateStyle('fontSize', v)} placeholder="16" /></StyleRow>
          <StyleRow label="Weight">
            <StyleSelect value={s.fontWeight} onChange={v => updateStyle('fontWeight', v)} options={[
              { value: '300', label: 'Light' }, { value: '400', label: 'Regular' }, { value: '500', label: 'Medium' },
              { value: '600', label: 'Semibold' }, { value: '700', label: 'Bold' }, { value: '800', label: 'Extrabold' },
            ]} />
          </StyleRow>
          <StyleRow label="Align">
            <StyleSelect value={s.textAlign || 'left'} onChange={v => updateStyle('textAlign', v)} options={[
              { value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }, { value: 'justify', label: 'Justify' },
            ]} />
          </StyleRow>
          <StyleRow label="Color"><ColorInput value={s.color} onChange={v => updateStyle('color', v)} /></StyleRow>
          <StyleRow label="Line ht"><PxInput value={s.lineHeight} onChange={v => updateStyle('lineHeight', v)} placeholder="1.5" unit="" /></StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Spacing">
          <SpacingControl label="Padding" style={s} onChange={(k, v) => updateStyle(k, v)} type="padding" />
          <Box sx={{ mt: 0.75 }}><SpacingControl label="Margin" style={s} onChange={(k, v) => updateStyle(k, v)} type="margin" /></Box>
          <Box sx={{ mt: 0.75 }}><DimensionControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Appearance">
          <ShadowControl label="Shadow" value={s.textShadow} onChange={v => updateStyle('textShadow', v)} />
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={el.visibleOn} onChange={v => onUpdate({ ...el, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}

// ─── ButtonInspector ──────────────────────────────────────────────────────────
export function ButtonInspector({ el, breakpoint = 'desktop', onUpdate, onClose }) {
  const s = getEffectiveStyle(el, breakpoint);
  const updateStyle = (key, val) => {
    if (breakpoint === 'desktop') { onUpdate({ ...el, style: { ...(el.style || {}), [key]: val } }); }
    else { onUpdate({ ...el, responsiveStyles: { ...(el.responsiveStyles || {}), [breakpoint]: { ...(el.responsiveStyles?.[breakpoint] || {}), [key]: val } } }); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<SmartButtonOutlined sx={{ fontSize: 14 }} />} label="Button" color="#00a882" onClose={onClose} breakpoint={breakpoint} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Content">
          <StyleRow label="Label"><StyleInput value={el.label} onChange={v => onUpdate({ ...el, label: v })} placeholder="Click Me" /></StyleRow>
          <StyleRow label="Link"><StyleInput value={el.href} onChange={v => onUpdate({ ...el, href: v })} placeholder="https://…" /></StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Typography">
          <StyleRow label="Size"><PxInput value={s.fontSize} onChange={v => updateStyle('fontSize', v)} placeholder="14" /></StyleRow>
          <StyleRow label="Weight">
            <StyleSelect value={s.fontWeight} onChange={v => updateStyle('fontWeight', v)} options={[
              { value: '400', label: 'Regular' }, { value: '500', label: 'Medium' }, { value: '600', label: 'Semibold' }, { value: '700', label: 'Bold' },
            ]} />
          </StyleRow>
          <StyleRow label="Align">
            <StyleSelect value={s.textAlign || 'left'} onChange={v => updateStyle('textAlign', v)} options={[
              { value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' },
            ]} />
          </StyleRow>
          <StyleRow label="Color"><ColorInput value={s.color} onChange={v => updateStyle('color', v)} /></StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Appearance">
          <StyleRow label="Background"><ColorInput value={s.background} onChange={v => updateStyle('background', v)} /></StyleRow>
          <Box sx={{ mt: 0.75 }}><RadiusControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
          <Box sx={{ mt: 0.75 }}><BorderControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
          <Box sx={{ mt: 0.75 }}><ShadowControl value={s.boxShadow} onChange={v => updateStyle('boxShadow', v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Spacing">
          <SpacingControl label="Padding" style={s} onChange={(k, v) => updateStyle(k, v)} type="padding" />
          <Box sx={{ mt: 0.75 }}><SpacingControl label="Margin" style={s} onChange={(k, v) => updateStyle(k, v)} type="margin" /></Box>
          <Box sx={{ mt: 0.75 }}><DimensionControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={el.visibleOn} onChange={v => onUpdate({ ...el, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}

// ─── ImageInspector ───────────────────────────────────────────────────────────
export function ImageInspector({ el, breakpoint = 'desktop', onUpdate, onClose }) {
  const s = getEffectiveStyle(el, breakpoint);
  const IMG_COLOR = '#e67e22';
  const updateStyle = (key, val) => {
    if (breakpoint === 'desktop') { onUpdate({ ...el, style: { ...(el.style || {}), [key]: val } }); }
    else { onUpdate({ ...el, responsiveStyles: { ...(el.responsiveStyles || {}), [breakpoint]: { ...(el.responsiveStyles?.[breakpoint] || {}), [key]: val } } }); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<ImageOutlined sx={{ fontSize: 14 }} />} label="Image" color={IMG_COLOR} onClose={onClose} breakpoint={breakpoint} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        <InspectorSection label="Source">
          <StyleRow label="Image">
            <MediaField value={el.src || ''} onChange={v => onUpdate({ ...el, src: v })} mediaType="image" />
          </StyleRow>
          <StyleRow label="Alt text"><StyleInput value={el.alt} onChange={v => onUpdate({ ...el, alt: v })} placeholder="Description" /></StyleRow>
          
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Size">
          <StyleRow label="Width"><StyleInput value={s.width} onChange={v => updateStyle('width', v)} placeholder="100%" /></StyleRow>
          <StyleRow label="Height"><StyleInput value={s.height} onChange={v => updateStyle('height', v)} placeholder="auto" /></StyleRow>
          <StyleRow label="Object fit">
            <StyleSelect value={s.objectFit || 'cover'} onChange={v => updateStyle('objectFit', v)} options={[
              { value: 'cover', label: 'Cover' }, { value: 'contain', label: 'Contain' }, { value: 'fill', label: 'Fill' }, { value: 'none', label: 'None' },
            ]} />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Appearance">
          <RadiusControl style={s} onChange={(k, v) => updateStyle(k, v)} />
          <Box sx={{ mt: 0.75 }}><StyleRow label="Opacity"><StyleInput value={s.opacity} onChange={v => updateStyle('opacity', v)} placeholder="1" /></StyleRow></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Spacing">
          <SpacingControl label="Padding" style={s} onChange={(k, v) => updateStyle(k, v)} type="padding" />
          <Box sx={{ mt: 0.75 }}><SpacingControl label="Margin" style={s} onChange={(k, v) => updateStyle(k, v)} type="margin" /></Box>
          <Box sx={{ mt: 0.75 }}><DimensionControl style={s} onChange={(k, v) => updateStyle(k, v)} /></Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={el.visibleOn} onChange={v => onUpdate({ ...el, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}

// ─── LeftPanelIdle internals ──────────────────────────────────────────────────

function slugify(str) {
  return (str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'page';
}

function validateSlug(val) {
  if (!val) return 'Slug cannot be empty';
  if (!/^[a-z0-9-]+$/.test(val)) return 'Only lowercase letters, numbers, and hyphens';
  if (val.startsWith('-') || val.endsWith('-')) return 'Cannot start or end with a hyphen';
  return '';
}

// Controlled input that commits on blur/Enter, syncs when `value` prop changes (page switch)
export function CommitInput({ value, onChange, placeholder, prefix, validate, monospace }) {
  const [local, setLocal]   = useState(value);
  const [error, setError]   = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => { setLocal(value); setError(''); }, [value]);

  const commit = () => {
    setFocused(false);
    if (validate) {
      const msg = validate(local);
      if (msg) { setError(msg); return; }
    }
    setError('');
    if (local !== value) onChange(local);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {prefix && (
        <Typography sx={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem', color: '#9ea8b3', pointerEvents: 'none', zIndex: 1, userSelect: 'none' }}>
          {prefix}
        </Typography>
      )}
      <Box
        component="input"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setLocal(value); e.target.blur(); } }}
        placeholder={placeholder}
        sx={{
          width: '100%', boxSizing: 'border-box',
          border: `1px solid ${error ? '#ef4444' : focused ? PRIMARY : '#e4e9f0'}`,
          borderRadius: '6px',
          pl: prefix ? '22px' : '8px', pr: '8px', py: '5px',
          fontSize: monospace ? '0.75rem' : '0.8rem',
          fontFamily: monospace ? 'monospace' : 'inherit',
          fontWeight: 500, color: '#1a1a2e',
          background: focused ? '#fff' : '#f8f9fb',
          outline: 'none',
          transition: 'border-color 0.15s, background 0.15s',
          '&::placeholder': { color: '#c0c8d4', fontWeight: 400 },
        }}
      />
      {error && <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', mt: 0.5, pl: 0.25 }}>{error}</Typography>}
    </Box>
  );
}

export function ToggleRow({ label, description, checked, onChange, icon, accentColor = PRIMARY }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {icon && (
          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: alpha(accentColor, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor, flexShrink: 0 }}>
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', lineHeight: 1.2 }}>{label}</Typography>
          {description && <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', mt: 0.25, lineHeight: 1.3 }}>{description}</Typography>}
        </Box>
      </Box>
      <Switch
        checked={!!checked}
        onChange={e => onChange(e.target.checked)}
        size="small"
        sx={{
          flexShrink: 0,
          '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor },
        }}
      />
    </Box>
  );
}

export function PageSettingsContent({ page, onUpdatePage, event, onSlugAvailabilityChange }) {
  if (!page) return null;

  const [slugAvailable, setSlugAvailable] = useState(null); // null=unchecked, true, false
  const [checkingSlug, setCheckingSlug]   = useState(false);
  const debounceRef = useRef(null);

  // Report availability upward whenever it changes
  useEffect(() => {
    onSlugAvailabilityChange?.(slugAvailable);
  }, [slugAvailable]); // eslint-disable-line 

  // Reset availability state when switching pages
  useEffect(() => { setSlugAvailable(null); }, [page._id]);

  const checkSlugAvailability = useCallback((slug) => {
    if (!event?._id || !slug) return;
    clearTimeout(debounceRef.current);
    setCheckingSlug(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/events/${event._id}/pages/check-slug`,
          { params: { slug, excludeId: page._isNew ? undefined : page._id } }
        );
        const available = res.data?.available ?? true;
        setSlugAvailable(available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 500);
  }, [event?._id, page._id, page._isNew]);

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Identity ── */}
      <InspectorSection label="Identity">
        <StyleRow label="Title">
          <CommitInput
            value={page.title || ''}
            onChange={title => onUpdatePage({ title })}
            placeholder="e.g. Home"
          />
        </StyleRow>
        <StyleRow label="Slug">
          <CommitInput
            value={page.slug || ''}
            onChange={slug => {
              const clean = slugify(slug);
              onUpdatePage({ slug: clean });
              setSlugAvailable(null);
              checkSlugAvailability(clean);
            }}
            placeholder="e.g. about"
            prefix="/"
            monospace
            validate={validateSlug}
          />
          {checkingSlug && (
            <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 0.5, pl: 0.25 }}>
              Checking availability…
            </Typography>
          )}
          {!checkingSlug && slugAvailable === false && (
            <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', mt: 0.5, pl: 0.25 }}>
              Slug name not available. Please use another.
            </Typography>
          )}
          {!checkingSlug && slugAvailable === true && (
            <Typography sx={{ fontSize: '0.65rem', color: '#22c55e', mt: 0.5, pl: 0.25 }}>
              Slug is available ✓
            </Typography>
          )}
        </StyleRow>
      </InspectorSection>

      <Divider sx={{ borderColor: '#f0f2f5' }} />

      {/* ── Visibility ── */}
      <InspectorSection label="Visibility">
        <ToggleRow
          label="Published"
          description="Visible to public visitors"
          checked={page.published}
          onChange={v => onUpdatePage({ published: v })}
          icon={<PublicOutlined sx={{ fontSize: 15 }} />}
          accentColor="#22c55e"
        />
        <Box sx={{ mt: 1 }}>
          <ToggleRow
            label="Default Page"
            description="Shown when no slug is provided"
            checked={page.isDefault}
            onChange={v => onUpdatePage({ isDefault: v })}
            icon={<StarOutlined sx={{ fontSize: 15 }} />}
            accentColor="#f59e0b"
          />
          {page.isDefault && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, px: 1, py: 0.6, background: alpha('#f59e0b', 0.07), borderRadius: 1.5 }}>
              <CheckCircleOutlined sx={{ fontSize: 12, color: '#f59e0b' }} />
              <Typography sx={{ fontSize: '0.67rem', color: '#b45309', fontWeight: 500 }}>
                This is the homepage of your event
              </Typography>
            </Box>
          )}
        </Box>
      </InspectorSection>

    </Box>
  );
}

// ─── TemplatesPalette ─────────────────────────────────────────────────────────
function TemplatesPalette({ onApplyTemplate }) {
  const [pending, setPending] = useState(null); // template waiting for confirm
  const thumbnails = useTemplateThumbnails(PAGE_TEMPLATES);

  const TEMPLATE_COLORS = [
    { bg: 'linear-gradient(135deg, #eef4ff 0%, #dce8fa 100%)', accent: '#1075BA' },
    { bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', accent: '#16a34a' },
    { bg: 'linear-gradient(135deg, #fff7ed 0%, #fee2e2 100%)', accent: '#ef4444' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #e8edf2' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Templates</Typography>
          <Typography variant="caption" color="text.secondary">Click to apply to this page</Typography>
        </Box>
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {PAGE_TEMPLATES.map((tpl, i) => {
            const { bg, accent } = TEMPLATE_COLORS[i % TEMPLATE_COLORS.length];
            const thumb = thumbnails[tpl.id];
            return (
              <Box
                key={tpl.id}
                onClick={() => setPending(tpl)}
                sx={{ borderRadius: 2, border: '1px solid #e4e9f0', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: accent, boxShadow: `0 0 0 2px ${accent}22`, transform: 'translateY(-1px)' } }}
              >
                {/* Thumbnail — real capture or fallback sketch */}
                <Box sx={{ height: 120, position: 'relative', overflow: 'hidden', background: thumb ? '#f0f4f8' : bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={tpl.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                    />
                  ) : (
                    /* Fallback sketch while generating */
                    <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box sx={{ height: 10, borderRadius: 1, background: accent, opacity: 0.7, width: '60%' }} />
                      <Box sx={{ height: 7, borderRadius: 1, background: accent, opacity: 0.35, width: '90%' }} />
                      <Box sx={{ height: 7, borderRadius: 1, background: accent, opacity: 0.25, width: '75%' }} />
                      <Box sx={{ mt: '4px', display: 'flex', gap: '4px' }}>
                        {[1, 2, 3].map(k => <Box key={k} sx={{ flex: 1, height: 28, borderRadius: 1, background: accent, opacity: 0.15 }} />)}
                      </Box>
                      <Box sx={{ height: 18, borderRadius: 8, background: accent, opacity: 0.5, width: '40%', alignSelf: 'center', mt: '4px' }} />
                      <Typography sx={{ fontSize: '0.6rem', color: accent, opacity: 0.6, textAlign: 'center', mt: 0.5 }}>Generating preview…</Typography>
                    </Box>
                  )}
                  <Box sx={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', color: '#fff', borderRadius: 1, px: 0.75, py: 0.25, fontSize: '0.6rem', fontWeight: 700 }}>
                    {tpl.sections.length} sections
                  </Box>
                </Box>
                {/* Info */}
                <Box sx={{ px: 1.5, py: 1.25, background: '#fff' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>{tpl.name}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', mt: 0.3, lineHeight: 1.4 }}>{tpl.description}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Confirm modal ── */}
      {pending && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(10,15,30,0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: 2,
        }}
          onClick={() => setPending(null)}
        >
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ background: '#fff', borderRadius: 3, width: '100%', maxWidth: 400, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
          >
            {/* Warning band */}
            <Box sx={{ background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)', borderBottom: '1px solid #fde68a', px: 3, pt: 3, pb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: '#fef3c7', border: '1.5px solid #fcd34d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AutoAwesomeOutlined sx={{ fontSize: 24, color: '#d97706' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
                  Apply "{pending.name}"?
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
                  This will replace all current content on this page with the template. This action cannot be undone.
                </Typography>
              </Box>
            </Box>
            {/* Actions */}
            <Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 1.5 }}>
              <Box
                onClick={() => setPending(null)}
                sx={{ flex: 1, py: 1.1, borderRadius: 2, border: '1px solid #e4e9f0', background: '#f8f9fb', color: '#5a6474', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', '&:hover': { background: '#f0f2f5' } }}
              >
                Cancel
              </Box>
              <Box
                onClick={() => { onApplyTemplate(pending); setPending(null); }}
                sx={{ flex: 1.4, py: 1.1, borderRadius: 2, background: PRIMARY, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, '&:hover': { opacity: 0.88 } }}
              >
                <AutoAwesomeOutlined sx={{ fontSize: 15 }} />
                Apply Template
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

// ─── LeftPanelIdle ────────────────────────────────────────────────────────────
/**
 * Shown in the left inspector when no element or section is selected.
 * Tabs: Elements (drag palette) | Templates
 */
export function LeftPanelIdle({ page, onUpdatePage, onDragStart, onApplyTemplate }) {
  const [activeTab, setActiveTab] = useState('elements');

  const TABS = [
    { key: 'elements',  label: 'Elements',  icon: <WidgetsOutlined      sx={{ fontSize: 14 }} /> },
    { key: 'templates', label: 'Templates', icon: <AutoAwesomeOutlined  sx={{ fontSize: 14 }} /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #e8edf2', background: '#fafbfc', px: 1, pt: 0.75, gap: 0.5, flexShrink: 0 }}>
        {TABS.map(tab => {
          const active = activeTab === tab.key;
          return (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6,
                py: 0.8, px: 1, borderRadius: '6px 6px 0 0', cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: active ? 700 : 500,
                color: active ? PRIMARY : '#9ea8b3',
                background: active ? '#fff' : 'transparent',
                borderBottom: active ? `2px solid ${PRIMARY}` : '2px solid transparent',
                transition: 'all 0.15s',
                '&:hover': { color: active ? PRIMARY : '#5a6474' },
              }}
            >
              <Box sx={{ color: 'inherit', display: 'flex', alignItems: 'center' }}>{tab.icon}</Box>
              {tab.label}
            </Box>
          );
        })}
      </Box>
      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {activeTab === 'elements'  && <ElementPalette onDragStart={onDragStart} />}
        {activeTab === 'templates' && <TemplatesPalette onApplyTemplate={onApplyTemplate} />}
      </Box>
    </Box>
  );
}